import { CopyButton } from "@bezoned/shared/components/copy-button.jsx";
import { useClipboard } from "@bezoned/shared/hooks/use-clipboard.js";
import { Html } from "@react-three/drei";
import type { MapControls } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useState } from "react";
import type { ElementRef, MutableRefObject } from "react";

interface CameraDebuggerProps {
  show?: boolean;
  orbitControlsRef: MutableRefObject<ElementRef<typeof MapControls> | null>;
}

// Camera debug component to display current camera values
export const CameraDebugger = ({
  show = false,
  orbitControlsRef,
}: CameraDebuggerProps) => {
  const { camera } = useThree();
  const clipboard = useClipboard();

  const [cameraInfo, setCameraInfo] = useState({
    azimuthAngle: "0.0000",
    polarAngle: "0.0000",
    position: ["0.00", "0.00", "0.00"],
    target: ["0.00", "0.00", "0.00"],
    zoom: "0.00",
  });

  useFrame(() => {
    if (orbitControlsRef.current && camera) {
      const controls = orbitControlsRef.current;

      setCameraInfo({
        azimuthAngle: controls.getAzimuthalAngle().toFixed(4),
        polarAngle: controls.getPolarAngle().toFixed(4),
        position: [
          camera.position.x.toFixed(2),
          camera.position.y.toFixed(2),
          camera.position.z.toFixed(2),
        ],
        target: [
          controls.target.x.toFixed(2),
          controls.target.y.toFixed(2),
          controls.target.z.toFixed(2),
        ],
        zoom: camera.zoom.toFixed(2),
      });
    }
  });

  if (!show) {
    return null;
  }

  return (
    <Html
      className="user-select-text fixed top-10 right-10 z-50 w-full max-w-sm min-w-[300px] rounded-lg bg-black/85 p-4 font-mono text-sm text-white"
      position={[0, 0, 0]}
    >
      <div className="mb-2 text-lg font-bold text-yellow-500">
        Camera Debugger
      </div>
      <div style={{ lineHeight: "1.6" }}>
        <div>
          <strong>Position:</strong> [{cameraInfo.position?.join(", ")}]
        </div>
        <div>
          <strong>Target:</strong> [{cameraInfo.target?.join(", ")}]
        </div>
        <div>
          <strong>Zoom:</strong> {cameraInfo.zoom}
        </div>
        <div className="mt-2 border-t border-gray-300 pt-2">
          <div>
            <strong>Polar Angle:</strong> {cameraInfo.polarAngle}
          </div>
          <div>
            <strong>Azimuth Angle:</strong> {cameraInfo.azimuthAngle}
          </div>
        </div>
        <div
          style={{
            borderTop: "1px solid #333",
            color: "#aaa",
            fontSize: "11px",
            marginTop: "10px",
            paddingTop: "10px",
          }}
        >
          Use mouse to adjust view, then copy these values
        </div>

        <CopyButton
          className="mt-2 w-full rounded-md border-none bg-green-500 p-2 font-bold text-black"
          clipboard={clipboard}
          tooltipDefault="Copy Values"
          tooltipEmptyValue="No values to copy"
          value={`// Camera Configuration \nposition: [${cameraInfo.position?.join(", ")}], target: [${cameraInfo.target?.join(", ")}], zoom: ${cameraInfo.zoom}, polarAngle: ${cameraInfo.polarAngle}, azimuthAngle: ${cameraInfo.azimuthAngle}`}
        />
      </div>
    </Html>
  );
};
